import {
  type PurchaseOption,
  VALID_PURCHASE_OPTIONS,
} from "@lib/api/checkout.types";

import { fbCapiContext, readCookie } from "./fbCapiContext";
import { segmentInstance } from "./segmentInstance";

// Closes the begin_checkout timing gap vs the legacy /doSignup. The
// legacy page runs an Angular controller that fires
// add_to_cart/select_item/begin_checkout within ~500ms of navigation
// (server-rendered HTML + sync controller bootstrap). The React
// /checkout previously fired these from a useEffect in checkout.tsx,
// which only runs after:
//   - main JS chunk downloads + parses
//   - i18next initializes (main.tsx awaits this before mounting React
//     to avoid the Fallback flash from the Suspense thrown by
//     useTranslation())
//   - React mounts, routes resolve, Checkout component renders
//
// On mobile that adds up to 2–6 seconds — long enough that bouncing
// users in the gap are silently missed in Convert's begin_checkout
// goal, biasing the variant comparison.
//
// Firing from main.tsx (before the i18n await) shrinks the gap to
// "main JS chunk parsed", typically 0.5–2s. Still not as fast as
// legacy's <500ms (we're gated on JS bundle download), but the
// closure is meaningful for slow-mobile bouncers.
//
// Tradeoff: at this point we don't yet have the productPricing API
// response, so the event payload here lacks `value`, `currency`
// (defaulted to USD), and per-item price/discount fields. GA4
// funnel-step goals fire on event name, not properties, so funnel
// counts are intact. Revenue-attribution destinations that key on
// `value` for begin_checkout lose some signal — accepted trade for
// the timing parity.
//
// Sets `window.__omgyesEarlyCheckoutFired` so the React-side useEffect
// in checkout.tsx skips a duplicate fire on the same page load.

declare global {
  interface Window {
    __omgyesEarlyCheckoutFired?: boolean;
  }
}

const CHECKOUT_SOURCE = { checkout_source: "react_checkout" } as const;

export const fireEarlyCheckoutTracking = (): void => {
  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  const isCheckout =
    path === "/checkout" ||
    path === "/checkout/" ||
    path.startsWith("/checkout/");
  if (!isCheckout) return;

  const params = new URLSearchParams(window.location.search);
  const rawOption = params.get("option") || "";
  if (!VALID_PURCHASE_OPTIONS.includes(rawOption as PurchaseOption)) {
    // checkout.tsx redirects to ?option=series for invalid options, but
    // that redirect happens after React mounts. We can't replicate that
    // logic safely from main.tsx (it would race with React Router), so
    // we just skip the early fire here and let the React-side useEffect
    // handle the fire post-redirect.
    return;
  }

  const discountCode =
    params.get("discount_source") || readCookie("discount_source") || null;

  const ecommerceProps = {
    ...CHECKOUT_SOURCE,
    // Currency defaulted to USD — the real currency comes from the
    // pricing API which hasn't resolved yet. International visitors
    // will see USD here even if they end up paying in their local
    // currency. The legacy doSignup events also default to USD in some
    // cases (per checkoutService.js), so this is the same trade-off.
    currency: "USD" as string,
    value: undefined as number | undefined,
    items: [
      {
        item_id: rawOption,
        item_name: rawOption,
        coupon: discountCode ?? "",
        discount: "",
        index: 0,
        price: undefined as number | undefined,
        quantity: 1,
      },
    ],
  };

  // Attach the Facebook _fbc/_fbp cookies to the Segment context — same
  // builder the React-side useAnalytics fire uses, so the two can't drift
  // (see fbCapiContext.ts / OMG-1889). This early fire wins the
  // __omgyesEarlyCheckoutFired dedup, so without it the "Initiate Checkout"
  // FB CAPI mapping (reads $.context.fbc) gets no click ID for begin_checkout.
  const fbContext = fbCapiContext();

  // Mirror the order in checkout.tsx so GA4 funnel reports keyed on
  // (add_to_cart → select_item → begin_checkout) stay consistent.
  segmentInstance.track("add_to_cart", ecommerceProps, fbContext);
  segmentInstance.track("select_item", ecommerceProps, fbContext);
  segmentInstance.track("begin_checkout", ecommerceProps, fbContext);

  window.__omgyesEarlyCheckoutFired = true;
};
